import {useEffect, useMemo, useState} from 'react'
import {defineMessages, useIntl} from 'react-intl'
import {InsurancePaymentsTab} from './policyFormTabs/InsurancePaymentsTab'
import {TFormAllValues} from '../../policies/policiesTypes'
import {FormikErrors, FormikTouched} from 'formik'
import {NodePolicy} from '../../../types/policiesApi'
import {STATUS_POLICY} from '../../../helpers/consts'
import {PolicyHolderTab} from './policyFormTabs/PolicyHolderTab'
import {FamilyMembersTab} from './policyFormTabs/FamilyMembersTab'
import {PolicyInsuranceTab} from './policyFormTabs/PolicyInsuranceTab'
import {MedicalTab} from './policyFormTabs/MedicalTab'
import {ExtraTab} from './policyFormTabs/ExtraTab'
import {IResStates} from '../../../types/statesEU'
import {TFormTabsPolicy} from '../soldTypes'
import {IResCarriersList} from '../../../types/carriers'
import {useGetUser} from '../../../store/userStore'
import {isLifeKind, TPolicyFormKind} from '../../../helpers/policyFormKind'

/**
 * Las pestañas dependen del `kind` del tipo de póliza. Vida no tiene "Members"
 * (no hay dependientes cubiertos) sino "Beneficiarios", y suma una pestaña médica.
 * El id `members` se reutiliza a propósito: así siguen funcionando el deep-link
 * `?tab=`, `pickFormTabByPath` y el mapeo de errores a pestañas.
 */
const ACA_TABS: TFormTabsPolicy[] = [
  'policy-holder',
  'members',
  'policy-insurance',
  'payment',
  'extras',
]

const LIFE_TABS: TFormTabsPolicy[] = [
  'policy-holder',
  'members',
  'policy-insurance',
  'medical',
  'payment',
  'extras',
]

const getFormTabs = (kind: TPolicyFormKind): TFormTabsPolicy[] =>
  isLifeKind(kind) ? LIFE_TABS : ACA_TABS

const TAB_ICONS: Record<TFormTabsPolicy, string> = {
  'policy-holder': 'bi-person',
  members: 'bi-people',
  'policy-insurance': 'bi-file-earmark-text',
  medical: 'bi-heart-pulse',
  payment: 'bi-credit-card',
  extras: 'bi-stars',
}

const tabLabels = defineMessages({
  'policy-holder': {id: 'policies.detail.form.tab.policyHolder', defaultMessage: 'Policy holder'},
  members: {id: 'policies.detail.form.tab.members', defaultMessage: 'Members'},
  'policy-insurance': {
    id: 'policies.detail.form.tab.policyInsurance',
    defaultMessage: 'Policy insurance',
  },
  medical: {id: 'policies.detail.form.tab.medical', defaultMessage: 'Medical'},
  payment: {id: 'policies.detail.form.tab.payment', defaultMessage: 'Payment'},
  extras: {id: 'policies.detail.form.tab.extras', defaultMessage: 'Extras'},
})

const beneficiariesLabel = {
  id: 'policies.detail.form.tab.beneficiaries',
  defaultMessage: 'Beneficiaries',
}

interface IPolicyProps {
  values: TFormAllValues
  errors: FormikErrors<TFormAllValues>
  touched: FormikTouched<TFormAllValues>
  handleChange: (field: string, value: string | boolean | null) => void
  handleBlur: (field: string) => void
  handleSubmit: () => void
  addMember: () => void
  removeMember: (index: number) => void
  loadingCreate: boolean
  loadingCarriers: boolean
  dataPolicy: NodePolicy
  refetch: () => void
  dataStates: IResStates | undefined
  dataCarriers: IResCarriersList | undefined
  updateZips?: number
  tabsUpdate?: TFormTabsPolicy,
  onSwitchWithHolder: (index: number) => void,
  canSwitchHolder: boolean,
  category?: string | null
  policyId?: string
  formKind?: TPolicyFormKind
}

export const PolicyForm: React.FC<IPolicyProps> = ({
  values,
  handleBlur,
  handleChange,
  handleSubmit,
  addMember,
  removeMember,
  errors,
  touched,
  loadingCreate,
  loadingCarriers,
  dataPolicy,
  refetch,
  dataStates,
  dataCarriers,
  updateZips,
  tabsUpdate,
  onSwitchWithHolder,
  canSwitchHolder,
  category,
  policyId,
  formKind = 'GENERIC',
}) => {
  const [formTab, setFormTab] = useState<TFormTabsPolicy>('policy-holder')
  const intl = useIntl()

  const isLife = isLifeKind(formKind)
  const formsTabs = useMemo(() => getFormTabs(formKind), [formKind])

  const userGroups = useGetUser((s) => s.user?.groups) || []
  const isAsesor = userGroups.includes('Asesor')
  // Asesores can't edit contact info (email/phone) once the policy is completed.
  const isContactEditDisabled = isAsesor && category === STATUS_POLICY.COMPLETED
  const isDisableEdit = category !== STATUS_POLICY.PENDING

  useEffect(() => {
    if (tabsUpdate) setFormTab(tabsUpdate)
  }, [tabsUpdate])

  // Al cambiar el tipo de póliza la pestaña activa puede dejar de existir
  // (p. ej. estabas en "Medical" y pasaste la póliza a ACA).
  useEffect(() => {
    if (!formsTabs.includes(formTab)) setFormTab('policy-holder')
  }, [formsTabs, formTab])

  const labelFor = (tab: TFormTabsPolicy) =>
    tab === 'members' && isLife
      ? intl.formatMessage(beneficiariesLabel)
      : intl.formatMessage(tabLabels[tab])

  const goToStep = (offset: number) => {
    const index = formsTabs.indexOf(formTab)
    const next = formsTabs[index + offset]
    if (next) setFormTab(next)
  }
  const onNextStep = () => goToStep(1)
  const onPrevStep = () => goToStep(-1)

  return (
    <div>
      <div className='policy-form-tabs'>
        {formsTabs.map((tab, index) => (
          <div
            onClick={() => {
              if (values.lead && Number(values.lead)) return
              setFormTab(tab)
            }}
            key={tab}
            className={tab === formTab ? 'tab active' : 'tab'}
            style={{flex: 1}}
          >
            <button className='btn btn-tabs' disabled={tab === formTab}>
              <span className='policy-form-tabs-number'>{index + 1}</span>
              <span style={{color: tab === formTab ? '#5d78ff' : 'gray'}}>{labelFor(tab)}</span>
            </button>
          </div>
        ))}
      </div>
      <div className='policy-form-tabs-container'>
        {formTab === 'policy-holder' && (
          <PolicyHolderTab
            onNextStep={onNextStep}
            values={values}
            errors={errors}
            touched={touched}
            handleBlur={handleBlur}
            handleChange={handleChange}
            isEdit={isDisableEdit}
            updateZips={updateZips}
            dataStates={dataStates}
            disableHolderEmail={isContactEditDisabled}
            disableHolderPhone={isContactEditDisabled}
            formKind={formKind}
          />
        )}
        {formTab === 'members' && (
          <FamilyMembersTab
            onNextStep={onNextStep}
            onPrevStep={onPrevStep}
            allValues={values}
            errors={errors}
            touched={touched}
            handleBlur={handleBlur}
            handleChange={handleChange}
            addMember={addMember}
            removeMember={removeMember}
            loadingCreate={loadingCreate}
            isDisableEdit={isDisableEdit}
            onSwitchWithHolder={onSwitchWithHolder}
            canSwitchHolder={canSwitchHolder}
            formKind={formKind}
          />
        )}
        {formTab === 'policy-insurance' && (
          <PolicyInsuranceTab
            onPrevStep={onPrevStep}
            onNextStep={onNextStep}
            allValues={values}
            handleBlur={handleBlur}
            handleChange={handleChange}
            errors={errors}
            touched={touched}
            dataCarriers={dataCarriers}
            dataStates={dataStates}
            loadingCarriers={loadingCarriers}
            loadingCreate={loadingCreate}
            isEdit={isDisableEdit}
            formKind={formKind}
          />
        )}
        {formTab === 'medical' && (
          <MedicalTab
            onPrevStep={onPrevStep}
            onNextStep={onNextStep}
            allValues={values}
            handleChange={handleChange}
            handleBlur={handleBlur}
            errors={errors}
            touched={touched}
            isDisableEdit={isDisableEdit}
          />
        )}
        {formTab === 'payment' && (
          <InsurancePaymentsTab
            onNextStep={onNextStep}
            onPrevStep={onPrevStep}
            handleChange={(field, value) => handleChange(`policyPayment.${field}`, value)}
            handleBlur={(field) => handleBlur(`policyPayment.${field}`)}
            allValues={values.policyPayment}
            errors={errors}
            touched={touched}
            policyId={policyId}
            formKind={formKind}
          />
        )}
        {formTab === 'extras' && (
          <ExtraTab
            onPrevStep={onPrevStep}
            handleSubmit={handleSubmit}
            handleChange={(field, value) => handleChange(`policyExtraInfo.${field}`, value)}
            handleBlur={(field) => handleBlur(`policyExtraInfo.${field}`)}
            allValues={values.policyExtraInfo}
            errors={errors}
            touched={touched}
            policyValues={{
              hasAutoPolicy: values.hasAutoPolicy,
              hasHomePolicy: values.hasHomePolicy,
              hasCarrierAccount: values.hasCarrierAccount,
              accountUsername: values.accountUsername,
              accountPassword: values.accountPassword,
              accountCreatedByClient: values.accountCreatedByClient,
            }}
            onPolicyChange={(field, value) => handleChange(field, value)}
            isDisableEdit={isDisableEdit}
            formKind={formKind}
          />
        )}
      </div>
    </div>
  )
}
